/**
 * Markdown, rendered safely.
 *
 * Documents are written by people and by agents, uploaded from anywhere, and read by everyone in
 * the organization — so this renderer never hands the browser a string of HTML. It parses the
 * Markdown into a small tree and builds React elements from it: raw HTML in a document comes out
 * as the text it is, and there is no dangerouslySetInnerHTML anywhere on the path.
 *
 * Links go out only to http, https and mailto, and always in a new tab without a referrer. Links
 * to other `.md` documents open that document here. Images become links rather than being
 * loaded: the page's CSP does not allow third-party images, and a document should not be able to
 * make every reader's browser call out to a server of its author's choosing.
 *
 * It covers what team documentation uses — CommonMark's blocks and inlines plus GFM tables, task
 * lists, strikethrough and bare URLs — and scans within bounded windows, so a hostile document
 * cannot make the page hang.
 *
 * This file parses and decides where links go; components/Markdown.tsx renders.
 */

// ------------------------------------------------------------------ tree

export type Inline =
  | { t: 'text'; v: string }
  | { t: 'code'; v: string }
  | { t: 'em' | 'strong' | 'del'; c: Inline[] }
  | { t: 'link'; href: string; title?: string; c: Inline[] }
  | { t: 'image'; src: string; alt: string }
  | { t: 'br' }

export type Align = 'left' | 'center' | 'right' | null

export type Block =
  | { t: 'heading'; level: number; c: Inline[]; id: string; text: string }
  | { t: 'paragraph'; c: Inline[] }
  | { t: 'code'; lang: string; v: string }
  | { t: 'quote'; c: Block[] }
  | { t: 'list'; ordered: boolean; start: number; tight: boolean; items: ListItem[] }
  | { t: 'table'; align: Align[]; head: Inline[][]; rows: Inline[][][] }
  | { t: 'hr' }

export interface ListItem {
  checked: boolean | null
  c: Block[]
}

/** How far a closing delimiter or bracket is looked for. Keeps the worst case linear-ish. */
const MAX_SCAN = 2000
/** Past this, a paragraph is shown as plain text: nobody writes one this long by hand. */
const MAX_INLINE = 20000
/** Quotes and lists nest at most this deep; deeper is shown as text. */
const MAX_DEPTH = 12

// ------------------------------------------------------------------ blocks

const FENCE = /^( {0,3})(`{3,}|~{3,})[ \t]*([^`\s]*)[^`]*$/
const HEADING = /^ {0,3}(#{1,6})(?:[ \t]+(.*?))?(?:[ \t]+#+)?[ \t]*$/
const HR = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/
const QUOTE = /^ {0,3}> ?(.*)$/
const LIST = /^( {0,3})([-*+]|\d{1,9}[.)])(?:([ \t]+)(.*))?$/
const TABLE_DELIMITER = /^ {0,3}\|?[ \t]*:?-+:?[ \t]*(?:\|[ \t]*:?-+:?[ \t]*)*\|?[ \t]*$/
const SETEXT = /^ {0,3}(=+|-+)[ \t]*$/
const TASK = /^\[([ xX])\][ \t]+/

interface Context {
  ids: Map<string, number>
}

export function parseMarkdown(source: string): Block[] {
  const lines = source
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.replace(/^\t+/, (tabs) => '    '.repeat(tabs.length)))
  return parseBlocks(lines, { ids: new Map() }, 0)
}

const isBlank = (line: string): boolean => line.trim() === ''
const indentOf = (line: string): number => line.length - line.trimStart().length

function startsBlock(line: string): boolean {
  return FENCE.test(line) || HEADING.test(line) || HR.test(line) || QUOTE.test(line) || startsInterruptingList(line)
}

/** A list interrupts a paragraph only when it plainly is one: a bullet with text, or "1.". */
function startsInterruptingList(line: string): boolean {
  const match = LIST.exec(line)
  if (!match || !match[4]?.trim()) return false
  return /^[-*+]$/.test(match[2] ?? '') || /^1[.)]$/.test(match[2] ?? '')
}

function parseBlocks(lines: string[], context: Context, depth: number): Block[] {
  const at = (n: number): string => lines[n] ?? ''
  const blocks: Block[] = []
  let i = 0

  while (i < lines.length) {
    const line = at(i)
    if (isBlank(line)) {
      i++
      continue
    }

    // Fenced code: everything up to the closing fence, untouched.
    const fence = FENCE.exec(line)
    if (fence) {
      const indent = (fence[1] ?? '').length
      const marker = (fence[2] ?? '')
      const closing = new RegExp(`^ {0,3}${marker[0] === '`' ? '`' : '~'}{${marker.length},}[ \\t]*$`)
      const body: string[] = []
      i++
      while (i < lines.length && !closing.test(at(i))) {
        body.push(at(i).slice(Math.min(indent, indentOf(at(i)))))
        i++
      }
      i++ // the closing fence, or past the end
      blocks.push({ t: 'code', lang: fence[3] ?? '', v: body.join('\n') })
      continue
    }

    // Indented code: four spaces in, kept as written.
    if (indentOf(line) >= 4) {
      const body: string[] = []
      while (i < lines.length && (isBlank(at(i)) || indentOf(at(i)) >= 4)) {
        body.push(at(i).slice(4))
        i++
      }
      while (body.length && isBlank(body[body.length - 1] ?? '')) body.pop()
      blocks.push({ t: 'code', lang: '', v: body.join('\n') })
      continue
    }

    const heading = HEADING.exec(line)
    if (heading) {
      blocks.push(headingBlock((heading[1] ?? '').length, heading[2] ?? '', context))
      i++
      continue
    }

    if (HR.test(line)) {
      blocks.push({ t: 'hr' })
      i++
      continue
    }

    if (QUOTE.test(line) && depth < MAX_DEPTH) {
      const inner: string[] = []
      while (i < lines.length) {
        const quoted = QUOTE.exec(at(i))
        if (quoted) inner.push(quoted[1] ?? '')
        // A lazy line carries on the quoted paragraph, as long as it is not a new block.
        else if (!isBlank(at(i)) && inner.length && !isBlank(inner[inner.length - 1] ?? '') && !startsBlock(at(i))) inner.push(at(i))
        else break
        i++
      }
      blocks.push({ t: 'quote', c: parseBlocks(inner, context, depth + 1) })
      continue
    }

    if (LIST.test(line) && depth < MAX_DEPTH) {
      const [list, next] = parseList(lines, i, context, depth)
      blocks.push(list)
      i = next
      continue
    }

    if (line.includes('|') && i + 1 < lines.length && TABLE_DELIMITER.test(at(i + 1))) {
      const head = splitRow(line)
      const align = splitRow(at(i + 1)).map(alignmentOf)
      if (head.length === align.length) {
        const rows: Inline[][][] = []
        i += 2
        while (i < lines.length && !isBlank(at(i)) && at(i).includes('|') && !startsBlock(at(i))) {
          const cells = splitRow(at(i))
          rows.push(align.map((_, column) => parseInline(cells[column] ?? '')))
          i++
        }
        blocks.push({ t: 'table', align, head: head.map((cell) => parseInline(cell)), rows })
        continue
      }
    }

    // A paragraph: lines until a blank or a new block. A line of === or --- under it makes it a heading.
    const paragraph: string[] = [line.trim()]
    i++
    let setext = 0
    while (i < lines.length && !isBlank(at(i))) {
      const underline = SETEXT.exec(at(i))
      if (underline) {
        setext = (underline[1] ?? '')[0] === '=' ? 1 : 2
        i++
        break
      }
      if (startsBlock(at(i))) break
      paragraph.push(at(i).trimStart())
      i++
    }
    const text = paragraph.join('\n')
    blocks.push(setext ? headingBlock(setext, text, context) : { t: 'paragraph', c: parseInline(text) })
  }

  return blocks
}

function parseList(lines: string[], start: number, context: Context, depth: number): [Block, number] {
  const at = (n: number): string => lines[n] ?? ''
  const firstMarker = LIST.exec(at(start))?.[2] ?? '-'
  const ordered = /^\d/.test(firstMarker)
  // A bullet is its own kind; numbers are told apart by their "." or ")".
  const kindOf = (marker: string): string => (/^\d/.test(marker) ? marker.slice(-1) : marker)
  const kind = kindOf(firstMarker)
  // Another item of this list: the same bullet, or numbers with the same "." or ")".
  const sibling = (line: string): RegExpExecArray | null => {
    const match = LIST.exec(line)
    const marker = match?.[2] ?? ''
    return match && /^\d/.test(marker) === ordered && kindOf(marker) === kind ? match : null
  }
  const items: ListItem[] = []
  let tight = true
  let i = start

  while (i < lines.length) {
    const match = sibling(at(i))
    if (!match) break

    const spaces = match[3]?.length ?? 1
    const contentIndent = (match[1] ?? '').length + (match[2] ?? '').length + (spaces > 4 ? 1 : spaces)
    const body: string[] = [match[4] ?? '']
    i++

    while (i < lines.length) {
      const line = at(i)
      if (isBlank(line)) {
        body.push('')
        i++
        continue
      }
      if (indentOf(line) >= contentIndent) {
        body.push(line.slice(contentIndent))
        i++
        continue
      }
      if (LIST.test(line)) break
      // A lazy continuation of the item's paragraph.
      if (!isBlank(body[body.length - 1] ?? '') && !startsBlock(line)) {
        body.push(line.trimStart())
        i++
        continue
      }
      break
    }

    // Blank lines at the end belong to the gap between items, not to this one.
    let trailing = 0
    while (body.length > 1 && isBlank(body[body.length - 1] ?? '')) {
      body.pop()
      trailing++
    }
    if (body.some(isBlank)) tight = false

    let checked: boolean | null = null
    const lead = body[0] ?? ''
    const task = TASK.exec(lead)
    if (task) {
      checked = task[1] !== ' '
      body[0] = lead.slice(task[0].length)
    }
    items.push({ checked, c: parseBlocks(body, context, depth + 1) })

    if (!sibling(at(i))) {
      // Whatever follows is not another item: the list ends, and the blanks go back to the page.
      i -= trailing
      break
    }
    if (trailing > 0) tight = false
  }

  return [{ t: 'list', ordered, start: ordered ? Number.parseInt(firstMarker, 10) : 1, tight, items }, i]
}

function headingBlock(level: number, text: string, context: Context): Block {
  const c = parseInline(text)
  const plain = plainText(c)
  const base = slugify(plain) || 'section'
  const seen = context.ids.get(base) ?? 0
  context.ids.set(base, seen + 1)
  return { t: 'heading', level, c, text: plain, id: seen ? `${base}-${seen}` : base }
}

function splitRow(line: string): string[] {
  let row = line.trim()
  if (row.startsWith('|')) row = row.slice(1)
  if (row.endsWith('|') && !row.endsWith('\\|')) row = row.slice(0, -1)
  const cells: string[] = []
  let current = ''
  for (let i = 0; i < row.length; i++) {
    if (row.charAt(i) === '\\' && row.charAt(i + 1) === '|') {
      current += '|'
      i++
    } else if (row.charAt(i) === '|') {
      cells.push(current.trim())
      current = ''
    } else current += row.charAt(i)
  }
  cells.push(current.trim())
  return cells
}

function alignmentOf(cell: string): Align {
  const left = cell.startsWith(':')
  const right = cell.endsWith(':')
  return left && right ? 'center' : right ? 'right' : left ? 'left' : null
}

// ------------------------------------------------------------------ inlines

const PUNCTUATION = '!"#$%&\'()*+,-./:;<=>?@[\\]^_`{|}~'
const AUTOLINK = /<((?:https?|mailto):[^\s<>]+|[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})>/y
const BARE_URL = /(?:https?:\/\/|www\.)[^\s<]+/y
const HARD_BREAK = / {2,}\n/y

export function parseInline(source: string): Inline[] {
  if (source.length > MAX_INLINE) return [{ t: 'text', v: source }]

  const out: Inline[] = []
  let text = ''
  const flush = (): void => {
    if (text) out.push({ t: 'text', v: text })
    text = ''
  }

  let i = 0
  while (i < source.length) {
    const ch = source.charAt(i)

    if (ch === '\\') {
      const next = source.charAt(i + 1)
      if (next === '\n') {
        flush()
        out.push({ t: 'br' })
        i += 2
        continue
      }
      if (next !== undefined && PUNCTUATION.includes(next)) {
        text += next
        i += 2
        continue
      }
    }

    if (ch === ' ') {
      HARD_BREAK.lastIndex = i
      const hard = HARD_BREAK.exec(source)
      if (hard) {
        flush()
        out.push({ t: 'br' })
        i += (hard[0] ?? '').length
        continue
      }
    }

    if (ch === '`') {
      let run = 1
      while (source.charAt(i + run) === '`') run++
      const close = findBacktickRun(source, i + run, run)
      if (close >= 0) {
        flush()
        let code = source.slice(i + run, close).replace(/\n/g, ' ')
        if (code.length > 2 && code.startsWith(' ') && code.endsWith(' ') && code.trim()) code = code.slice(1, -1)
        out.push({ t: 'code', v: code })
        i = close + run
      } else {
        text += '`'.repeat(run)
        i += run
      }
      continue
    }

    if (ch === '<') {
      AUTOLINK.lastIndex = i
      const auto = AUTOLINK.exec(source)
      if (auto) {
        flush()
        const target = (auto[1] ?? '')
        const href = /^(?:https?|mailto):/i.test(target) ? target : `mailto:${target}`
        out.push({ t: 'link', href, c: [{ t: 'text', v: target }] })
        i += (auto[0] ?? '').length
        continue
      }
    }

    if (ch === '!' && source.charAt(i + 1) === '[') {
      const link = parseLinkAt(source, i + 1)
      if (link) {
        flush()
        out.push({ t: 'image', src: link.href, alt: plainText(parseInline(link.label)) })
        i = link.end
        continue
      }
    }

    if (ch === '[') {
      const link = parseLinkAt(source, i)
      if (link) {
        flush()
        out.push({ t: 'link', href: link.href, title: link.title, c: parseInline(link.label) })
        i = link.end
        continue
      }
    }

    if ((ch === 'h' || ch === 'w') && (i === 0 || /[\s(*_~]/.test(source.charAt(i - 1)))) {
      BARE_URL.lastIndex = i
      const bare = BARE_URL.exec(source)
      if (bare) {
        const url = trimUrl(bare[0] ?? '')
        if (url.length > 'www.'.length) {
          flush()
          out.push({ t: 'link', href: url.startsWith('www.') ? `https://${url}` : url, c: [{ t: 'text', v: url }] })
          i += url.length
          continue
        }
      }
    }

    if (ch === '*' || ch === '_' || ch === '~') {
      const emphasis = parseEmphasis(source, i)
      if (emphasis) {
        flush()
        out.push(emphasis.node)
        i = emphasis.end
        continue
      }
      // An unmatched run is literal, all of it: trying each character again finds nothing new.
      let run = 1
      while (source.charAt(i + run) === ch) run++
      text += ch.repeat(run)
      i += run
      continue
    }

    text += ch
    i++
  }

  flush()
  return out
}

function findBacktickRun(source: string, from: number, length: number): number {
  const limit = Math.min(source.length, from + MAX_SCAN * 5)
  for (let i = from; i < limit; i++) {
    if (source.charAt(i) !== '`') continue
    let run = 1
    while (source.charAt(i + run) === '`') run++
    if (run === length) return i
    i += run - 1
  }
  return -1
}

/** Drops punctuation that ends a sentence rather than the URL, and a ")" the URL did not open. */
function trimUrl(url: string): string {
  let end = url.length
  for (;;) {
    const last = url.charAt(end - 1)
    if (/[.,:;!?'"*_~]/.test(last)) end--
    else if (last === ')') {
      const opens = url.slice(0, end).split('(').length - 1
      const closes = url.slice(0, end).split(')').length - 1
      if (closes > opens) end--
      else break
    } else break
  }
  return url.slice(0, end)
}

interface ParsedLink {
  label: string
  href: string
  title?: string
  end: number
}

/** `[label](destination "title")`, starting at the "[". Null when it is not a link. */
function parseLinkAt(source: string, start: number): ParsedLink | null {
  const limit = Math.min(source.length, start + MAX_SCAN)
  let depth = 0
  let close = -1
  for (let i = start; i < limit; i++) {
    const ch = source.charAt(i)
    if (ch === '\\') {
      i++
      continue
    }
    if (ch === '`') {
      let run = 1
      while (source.charAt(i + run) === '`') run++
      const end = findBacktickRun(source, i + run, run)
      if (end >= 0) i = end + run - 1
      continue
    }
    if (ch === '[') depth++
    else if (ch === ']') {
      depth--
      if (depth === 0) {
        close = i
        break
      }
    }
  }
  if (close < 0 || source.charAt(close + 1) !== '(') return null

  let i = close + 2
  while (source.charAt(i) === ' ' || source.charAt(i) === '\n') i++

  let href = ''
  if (source.charAt(i) === '<') {
    const end = source.indexOf('>', i)
    if (end < 0 || end - i > MAX_SCAN || source.slice(i, end).includes('\n')) return null
    href = source.slice(i + 1, end)
    i = end + 1
  } else {
    let parens = 0
    const begin = i
    while (i < source.length && i - begin < MAX_SCAN) {
      const ch = source.charAt(i)
      if (ch === '\\' && i + 1 < source.length) {
        i += 2
        continue
      }
      if (/\s/.test(ch)) break
      if (ch === '(') parens++
      else if (ch === ')') {
        if (parens === 0) break
        parens--
      }
      i++
    }
    href = source.slice(begin, i).replace(/\\([^A-Za-z0-9\s])/g, '$1')
  }

  while (source.charAt(i) === ' ' || source.charAt(i) === '\n') i++
  let title: string | undefined
  const quote = source.charAt(i)
  if (quote === '"' || quote === "'" || quote === '(') {
    const closing = quote === '(' ? ')' : quote
    const end = source.indexOf(closing, i + 1)
    if (end < 0 || end - i > MAX_SCAN) return null
    title = source.slice(i + 1, end)
    i = end + 1
    while (source.charAt(i) === ' ' || source.charAt(i) === '\n') i++
  }
  if (source.charAt(i) !== ')') return null

  return { label: source.slice(start + 1, close), href, title, end: i + 1 }
}

function parseEmphasis(source: string, start: number): { node: Inline; end: number } | null {
  const ch = source.charAt(start)

  if (ch === '~') {
    if (source.charAt(start + 1) !== '~' || source.charAt(start + 2) === '~') return null
    const open = start + 2
    if (!source.charAt(open) || /\s/.test(source.charAt(open))) return null
    const limit = Math.min(source.length, open + MAX_SCAN)
    for (let k = open + 1; k < limit; k++) {
      if (source.charAt(k) === '~' && source.charAt(k + 1) === '~' && source.charAt(k + 2) !== '~' && !/\s/.test(source.charAt(k - 1))) {
        return { node: { t: 'del', c: parseInline(source.slice(open, k)) }, end: k + 2 }
      }
    }
    return null
  }

  let run = 1
  while (source.charAt(start + run) === ch) run++
  // A run of three opens both: strong around emphasis.
  const size = run >= 3 ? 3 : run
  const open = start + size
  const after = source.charAt(open)
  if (!after || /\s/.test(after)) return null
  if (ch === '_' && start > 0 && /[\p{L}\p{N}]/u.test(source.charAt(start - 1))) return null

  const limit = Math.min(source.length, open + MAX_SCAN)
  for (let k = open; k < limit; k++) {
    const c = source.charAt(k)
    if (c === '\\') {
      k++
      continue
    }
    if (c === '`') {
      let ticks = 1
      while (source.charAt(k + ticks) === '`') ticks++
      const end = findBacktickRun(source, k + ticks, ticks)
      if (end >= 0) k = end + ticks - 1
      continue
    }
    if (c !== ch) continue

    let closeRun = 1
    while (source.charAt(k + closeRun) === ch) closeRun++
    const before = source.charAt(k - 1)
    const following = source.charAt(k + closeRun)
    const canClose =
      k > open && !/\s/.test(before) && !(ch === '_' && following !== undefined && /[\p{L}\p{N}]/u.test(following))

    if (canClose && closeRun >= size) {
      // Closed by the first `size` characters of the run; any left over read as text after it.
      const inner = source.slice(open, k)
      const end = k + size
      const node: Inline =
        size === 3
          ? { t: 'strong', c: [{ t: 'em', c: parseInline(inner) }] }
          : { t: size === 2 ? 'strong' : 'em', c: parseInline(inner) }
      return { node, end }
    }
    if (canClose && size === 1 && closeRun === 2) {
      // A nested strong inside emphasis: step over it.
      k += 1
      continue
    }
    k += closeRun - 1
  }
  return null
}

export function plainText(nodes: Inline[]): string {
  return nodes
    .map((node) => {
      switch (node.t) {
        case 'text':
        case 'code':
          return node.v
        case 'image':
          return node.alt
        case 'br':
          return ' '
        default:
          return plainText(node.c)
      }
    })
    .join('')
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\p{L}\p{N}\s-]/gu, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 80)
}

/** The headings of a document, for an outline. */
export function outlineOf(blocks: Block[]): Array<{ id: string; level: number; text: string }> {
  return blocks.flatMap((block) => (block.t === 'heading' ? [{ id: block.id, level: block.level, text: block.text }] : []))
}

// ------------------------------------------------------------------ links

export type Target =
  | { kind: 'external'; href: string }
  | { kind: 'anchor'; id: string }
  | { kind: 'doc'; path: string; anchor?: string }
  | { kind: 'none' }

/**
 * Where a link may go. Anything with a scheme other than http, https or mailto — javascript:,
 * data:, vbscript:, file: — goes nowhere, however it is spelled: control characters and spaces a
 * browser would ignore are removed before the scheme is read.
 */
export function targetOf(rawHref: string, currentPath?: string): Target {
  // eslint-disable-next-line no-control-regex
  const compact = rawHref.replace(/[\u0000- \u007f-\u009f]/g, '')
  if (!compact) return { kind: 'none' }

  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(compact)
  if (scheme) {
    const name = (scheme[1] ?? '').toLowerCase()
    return name === 'http' || name === 'https' || name === 'mailto' ? { kind: 'external', href: compact } : { kind: 'none' }
  }
  if (compact.startsWith('//') || compact.startsWith('\\\\')) return { kind: 'none' }

  // No scheme: an anchor or a path, read as written.
  const href = rawHref.trim()
  if (href.startsWith('#')) return { kind: 'anchor', id: slugify(decodeSafe(href.slice(1))) }

  const [pathPart = '', anchor] = href.split('#', 2)
  if (!/\.md$/i.test(pathPart)) return { kind: 'none' }
  const path = resolvePath(currentPath ?? '', decodeSafe(pathPart))
  return path ? { kind: 'doc', path, anchor: anchor ? slugify(decodeSafe(anchor)) : undefined } : { kind: 'none' }
}

function decodeSafe(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/** A link's path against the folder of the document it is in. "/x.md" is from the root. */
export function resolvePath(currentPath: string, href: string): string | null {
  const segments = href.startsWith('/') ? [] : currentPath.split('/').slice(0, -1)
  for (const part of href.split('/')) {
    if (part === '' || part === '.') continue
    if (part === '..') {
      if (segments.length === 0) return null
      segments.pop()
    } else segments.push(part)
  }
  return segments.length ? segments.join('/') : null
}
